import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { getOrphanProducts, createProduct } from "../../../server/Product";
import { getFiltersWithItems, getFilters } from "../../../server/Filter";
import PopUp from "../../containers/PopUp";
import "./Products.scss";

const productSchema = yup.object({
  ProductName: yup.string().required("נדרש שם מוצר").max(35),
  Barcode: yup.string().required("נדרש ברקוד"),
  BrandId: yup.string().required("נדרש מותג"),
  PackageQuantity: yup.string().required("נדרש כמות באריזה"),
  WeightQuantity: yup.string().required("נדרש משקל"),
  CategoryId: yup.string().required("נדרשת קטגוריה"),
  StorageModeId: yup.string().required("נדרש מצב אחסון"),
  DairyId: yup.string().required("נדרש סוג"),
  KashrutId: yup.string().required("נדרשת כשרות"),
  ProviderId: yup.string().required("נדרש ספק"),
  UnitMeasureId: yup.string().required("נדרש יחידת משקל"),
});

/* -------------------- initial product -------------------- */
const emptyProduct = {
  ProductName: "",
  Barcode: "",
  BrandId: "",
  PackageQuantity: "",
  WeightQuantity: "",
  CategoryId: "",
  StorageModeId: "",
  DairyId: "",
  KashrutId: "",
  ProviderId: "",
  UnitMeasureId: "",
};

/* -------------------- FormField -------------------- */
const FormField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  options = [],
  readOnly = false,
}) => {
  const isSelect = type === "select";

  return (
    <div className="form-group">
      <label className="label">
        {label} <span className="required">*</span>
      </label>

      {isSelect && !readOnly ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          className={`select-input ${error ? "input-error" : ""}`}
        >
          <option value="">בחר {label}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className={`input ${error ? "input-error" : ""} ${
            readOnly ? "readonly-input" : ""
          }`}
          style={
            readOnly
              ? { backgroundColor: "var(--hz-bg)", cursor: "not-allowed" }
              : {}
          }
        />
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

/* -------------------- CreateProductPopUp -------------------- */
const CreateProductPopUp = ({
  isOpen,
  onClose,
  initialBarcode,
  initialCategoryId,
  categoryName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [newProduct, setNewProduct] = useState({ ...emptyProduct });
  const [filtersFromServer, setFiltersFromServer] = useState({
    brand: [],
    "sub-brand": [],
    kashrut: [],
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* -------------------- fetch filters -------------------- */
  const fetchAll = useCallback(async () => {
    try {
      const [brandsRes, kashrutRes, subBrandRes] = await Promise.all([
        getFiltersWithItems("brand"),
        getFiltersWithItems("kashrut"),
        getFiltersWithItems("sub-brand"),
      ]);

      const mapOptions = (arr) =>
        (arr || []).map((f) => ({
          id: f.id || f.Id,
          name: (f.title || f.Name || "").replace(/^hamazon-/, ""), // מסיר hamazon-
        }));

      setFiltersFromServer({
        brand: mapOptions(brandsRes.data),
        kashrut: mapOptions(kashrutRes.data),
        "sub-brand": mapOptions(subBrandRes.data),
      });

      console.log("filters loaded:", {
        brand: brandsRes.data,
        subBrand: subBrandRes.data,
        kashrut: kashrutRes.data,
      });
    } catch (err) {
      console.error("Failed to load filters:", err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* -------------------- redux -------------------- */
  const {
    filterList,
    categories,
    parameters: { p_provider, p_storage_mode, p_unit_measure },
  } = useSelector((state) => ({
    filterList: state.displayProduct.filterList,
    categories: state.category.categories.filter((cat) => cat.FatherId === 0),
    parameters: state.parameters,
  }));

  useEffect(() => {
    dispatch(getFilters());
    dispatch(getOrphanProducts());
  }, [dispatch]);

  /* -------------------- modal lifecycle -------------------- */
  useEffect(() => {
    setIsModalOpen(isOpen);
    if (isOpen) {
      setNewProduct((prev) => ({
        ...prev,
        Barcode: initialBarcode || "",
        CategoryId: initialCategoryId || "",
      }));
    }
  }, [isOpen, initialBarcode, initialCategoryId]);

  const closeModal = () => {
    setIsModalOpen(false);
    setNewProduct({ ...emptyProduct });
    setFormErrors({});
    if (onClose) onClose();
  };

  const validateForm = () => {
    try {
      productSchema.validateSync(newProduct, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};
      err.inner.forEach((error) => {
        errors[error.path] = error.message;
      });
      return errors;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setNewProduct({
      ...newProduct,
      [name]: value,
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const response = await createProduct(newProduct);
      if (response.data?.productMkt) {
        closeModal();
        navigate(`/edit-product/${response.data.productMkt}/information`);
      }
    } catch (error) {
      console.error("Error creating product:", error);
      setFormErrors({ general: "שגיאה ביצירת מוצר" });
    }
  };

  const fieldSources = {
    brands: filtersFromServer.brand,
    subBrands: filtersFromServer["sub-brand"],
    kashrut: filtersFromServer.kashrut,
    dairy: filterList[2]?.Items || [],
    providers: p_provider || [],
    storageModes: p_storage_mode || [],
    categories: categories || [],
    unitMeasures: p_unit_measure || [],
  };

  if (!isModalOpen) return null;

  return (
    <PopUp width={700} close={closeModal}>
      <div className="modal-content">
        <h3 className="modal-title">יצירת מוצר חדש</h3>

        <form onSubmit={handleSubmit} className="product-form">
          {formErrors.general && (
            <div className="error-message general-error">
              {formErrors.general}
            </div>
          )}

          <div className="grid-container">
            <FormField
              label="שם המוצר"
              name="ProductName"
              value={newProduct.ProductName}
              onChange={handleInputChange}
              error={formErrors.ProductName}
            />

            <FormField
              label="ספק"
              name="ProviderId"
              type="select"
              value={newProduct.ProviderId}
              onChange={handleInputChange}
              error={formErrors.ProviderId}
              options={fieldSources.providers}
            />
          </div>

          <div className="grid-container">
            <FormField
              label="ברקוד"
              name="Barcode"
              value={newProduct.Barcode}
              onChange={handleInputChange}
              error={formErrors.Barcode}
            />

            <FormField
              label="מותג"
              name="BrandId"
              type="select"
              value={newProduct.BrandId}
              onChange={handleInputChange}
              error={formErrors.BrandId}
              options={fieldSources.brands}
            />
          </div>

          <div className="grid-container">
            <FormField
              label="משקל"
              name="WeightQuantity"
              value={newProduct.WeightQuantity}
              onChange={handleInputChange}
              error={formErrors.WeightQuantity}
            />

            <FormField
              label="יחידת משקל"
              name="UnitMeasureId"
              type="select"
              value={newProduct.UnitMeasureId}
              onChange={handleInputChange}
              error={formErrors.UnitMeasureId}
              options={fieldSources.unitMeasures}
            />
          </div>

          <div className="grid-container">
            <FormField
              label="כמות באריזה"
              name="PackageQuantity"
              value={newProduct.PackageQuantity}
              onChange={handleInputChange}
              error={formErrors.PackageQuantity}
            />

            <FormField
              label="קטגוריה"
              name="CategoryId"
              type={categoryName ? "text" : "select"}
              readOnly={!!categoryName}
              value={categoryName || newProduct.CategoryId}
              options={fieldSources.categories}
              onChange={handleInputChange}
              error={formErrors.CategoryId}
            />
            {categoryName && (
              <input
                type="hidden"
                name="CategoryId"
                value={newProduct.CategoryId}
              />
            )}
          </div>

          <div className="grid-container">
            <FormField
              label="מצב אחסון"
              name="StorageModeId"
              type="select"
              value={newProduct.StorageModeId}
              onChange={handleInputChange}
              error={formErrors.StorageModeId}
              options={fieldSources.storageModes}
            />

            <FormField
              label="סוג"
              name="DairyId"
              type="select"
              value={newProduct.DairyId}
              onChange={handleInputChange}
              error={formErrors.DairyId}
              options={fieldSources.dairy}
            />

            <FormField
              label="כשרות"
              name="KashrutId"
              type="select"
              value={newProduct.KashrutId}
              onChange={handleInputChange}
              error={formErrors.KashrutId}
              options={fieldSources.kashrut}
            />
          </div>

          <div className="buttons-container">
            <button type="submit" className="save-button">
              שמור מוצר
            </button>
          </div>
        </form>
      </div>
    </PopUp>
  );
};

export default CreateProductPopUp;
